import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingBag, Menu, X, Trash2, ArrowRight } from 'lucide-react';
import './App.css';
import NotFound from './components/NotFound';
import { createCheckout } from './fourthwall';
import ProductPage from './ProductPage';
import { CookieConsent } from './components/CookieConsent';
import BrowseAll from './BrowseAll';
import { CustomSelect } from './components/CustomUI'; 
import { Contact, Terms, Privacy, Returns } from './components/LegalPages';
import { ShopProvider, useShop } from './context/ShopContext';
import SnowBackground from './components/SnowBackground';
import { AnimatedPrice } from './components/AnimatedPrice';

const Marquee = () => (
  <div className="marquee-container">
    <div className="marquee-content">
      <span>KNOWN TO FEW • VANTAGE WORLDWIDE • QUALITY GUARANTEED • </span>
      <span>KNOWN TO FEW • VANTAGE WORLDWIDE • QUALITY GUARANTEED • </span>
      <span>KNOWN TO FEW • VANTAGE WORLDWIDE • QUALITY GUARANTEED • </span>
      <span>KNOWN TO FEW • VANTAGE WORLDWIDE • QUALITY GUARANTEED • </span>
    </div>
  </div>
);

const PageWrapper = ({ children }) => {
  const location = useLocation();
  return (
    <div className="page-transition-wrapper" key={location.pathname}>
      {children}
    </div>
  );
};

// --- NEW COMPONENT: CART ITEM (Handles Animation) ---
const CartItem = ({ item, currency, getSymbol, getItemPrice, onRemove }) => {
  const [isExiting, setIsExiting] = useState(false);

  const handleRemoveClick = () => {
    setIsExiting(true);
    // Wait for animation (500ms) before actually removing data
    setTimeout(() => {
      onRemove(item.variantId);
    }, 500);
  };

  return (
    <div key={item.variantId} className={`cart-item ${isExiting ? 'exiting' : ''}`}>
      <img src={item.image} alt={item.name} />
      
      <div className="cart-item-info">
        <h4>{item.name}</h4>
        
        <p className="cart-item-size">Size: {item.variantName}</p>
        
        <div className="cart-item-price-row">
           {item.quantity} x <AnimatedPrice price={getItemPrice(item)} currency={currency} />
        </div>
      </div>

      <Trash2 size={18} className="remove-btn" onClick={handleRemoveClick} />
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { productsList, addToCart, currency } = useShop();
  
  const getSymbol = (code) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: '$', AUD: '$' };
    return symbols[code] || code;
  };
  
  const featuredProducts = (productsList && Array.isArray(productsList)) 
    ? productsList.slice(0, 3).map(p => {
        const variant = p.variants[0];
        const priceObj = variant?.prices?.[currency] || variant?.prices?.['USD'] || { value: 0 };
        
        return {
          ...p,
          image1: p.images && p.images[0] ? p.images[0].url : '',
          image2: p.images && p.images[1] ? p.images[1].url : (p.images[0]?.url || ''),
          price: priceObj.value,
          variantId: variant?.id,
          variantName: variant?.name
        };
      }) 
    : [];

  return (
    <>
      <section className="hero">
        <div className="hero-bg"></div>
        <div className="hero-content">
          <h1 className="fade-in">Known to Few</h1>
          <Link to="/collections/all">
            <button className="btn fade-in delay-1">Explore The Collection</button>
          </Link>
        </div>
      </section>

      <Marquee />

      <section id="products" className="products-section">
        <div className="section-header">
          <h2>Latest Drops</h2>
          <Link to="/collections/all">
            <button className="btn btn-outline">View All</button>
          </Link>
        </div>
        
        <div className="product-grid">
          {featuredProducts.map((product) => (
            <div key={product.id} className="product-card" onClick={() => navigate(`/product/${product.slug}`)}>
              <div className="product-image-wrapper">
                <img src={product.image1} alt={product.name} className="primary" />
                <img src={product.image2} alt={product.name} className="secondary" />
                <button 
                  className="quick-add-btn"
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    addToCart({
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      currency: currency,
                      image: product.image1,
                      variantId: product.variantId,
                      variantName: product.variantName
                    });
                  }}
                >
                  <ArrowRight size={20} />
                </button>
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <div className="product-price">
                  <AnimatedPrice price={product.price} currency={currency} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="newsletter">
        <h2>JOIN THE NEWSLETTER</h2>
        <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
          <input type="email" placeholder="Email address" className="newsletter-input" />
          <button className="btn">Subscribe</button>
        </form>
      </section>
    </>
  );
};

const Storefront = () => {
  const { 
    currency, setCurrency, 
    cart, removeFromCart, 
    isCartOpen, setIsCartOpen,
    isLoaded, productsList 
  } = useShop();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCheckout = async () => {
    setIsCheckingOut(true);
    try {
      const checkoutUrl = await createCheckout(cart, currency);
      window.location.href = checkoutUrl;
    } catch (error) {
      alert("Error creating checkout.");
      setIsCheckingOut(false);
    }
  };

  const currencyOptions = [
    { value: 'USD', label: '$ USD' },
    { value: 'EUR', label: '€ EUR' },
    { value: 'GBP', label: '£ GBP' },
    { value: 'CAD', label: '$ CAD' },
    { value: 'AUD', label: '$ AUD' },
  ];

  const getSymbol = (code) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: '$', AUD: '$' };
    return symbols[code] || code;
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const product = productsList.find(p => p.variants.some(v => v.id === item.variantId));
      const variant = product?.variants.find(v => v.id === item.variantId);
      const priceVal = variant?.prices?.[currency]?.value || item.price;
      return total + (priceVal * item.quantity);
    }, 0).toFixed(2);
  };

  const getItemPrice = (item) => {
    const product = productsList.find(p => p.variants.some(v => v.id === item.variantId));
    const variant = product?.variants.find(v => v.id === item.variantId);
    return variant?.prices?.[currency]?.value || item.price;
  }

  if (!isLoaded) return (
    <div className="loading-screen">
      <div className="loader-shape"></div>
    </div>
  );

  return (
    <div className="App">
      
      <SnowBackground />

      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
        <div className="menu-icon-mobile" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu color="white" size={28} />
        </div>

        <div className="logo-container">
          <Link to="/" className="logo-text">VANTAGE</Link>
        </div>

        <div className="nav-right-group">
          <div className="nav-links">
            <Link to="/">Home</Link>
            <Link to="/collections/all">Browse All</Link>
          </div>
          
          <div className="nav-actions">
            <CustomSelect 
              options={currencyOptions} 
              value={currency} 
              onChange={setCurrency} 
            />
            <div className="cart-icon-wrapper" onClick={() => setIsCartOpen(true)}>
              <ShoppingBag size={22} />
              {cart.length > 0 && <span className="cart-count">{cart.reduce((a,b)=>a+b.quantity,0)}</span>}
            </div>
          </div>
        </div>
      </nav>

      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="close-menu" onClick={() => setIsMobileMenuOpen(false)}>
          <X color="white" size={32} />
        </div>
        <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
        <Link to="/collections/all" onClick={() => setIsMobileMenuOpen(false)}>Browse All</Link>
      </div>

      <div className={`cart-overlay ${isCartOpen ? 'open' : ''}`} onClick={() => setIsCartOpen(false)}></div>
      <div className={`cart-sidebar ${isCartOpen ? 'open' : ''}`}>
        <div className="cart-header">
          <h3>Your Cart</h3>
          <X className="cursor-pointer" onClick={() => setIsCartOpen(false)} />
        </div>
        <div className="cart-items custom-scrollbar">
          {cart.length === 0 && <p className="empty-cart-msg">Your cart is empty.</p>}
          
          {/* USE NEW CART ITEM COMPONENT */}
          {cart.map((item) => (
            <CartItem 
              key={item.variantId} 
              item={item}
              currency={currency}
              getSymbol={getSymbol}
              getItemPrice={getItemPrice}
              onRemove={removeFromCart}
            />
          ))}
        </div>
        <div className="cart-footer">
          <div className="cart-total">
            <span>TOTAL</span>
            <AnimatedPrice price={calculateTotal()} currency={currency} />
          </div>
          <button className="btn checkout-btn" onClick={handleCheckout} disabled={isCheckingOut}>
            {isCheckingOut ? "REDIRECTING..." : "CHECKOUT"}
          </button>
        </div>
      </div>

      <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <PageWrapper>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/collections/all" element={<BrowseAll />} />
            <Route path="/product/:slug" element={<ProductPage />} />
            <Route path="/pages/contact" element={<Contact />} />
            <Route path="/pages/terms-of-service" element={<Terms />} />
            <Route path="/pages/privacy-policy" element={<Privacy />} />
            <Route path="/pages/returns-faq" element={<Returns />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </PageWrapper>
      </main>

      <footer>
        <div className="footer-links">
            <Link to="/collections/all">Shop</Link>
            <Link to="/pages/contact">Contact</Link>
            <Link to="/pages/terms-of-service">Terms of Service</Link>
            <Link to="/pages/privacy-policy">Privacy Policy</Link>
            <Link to="/pages/returns-faq">Returns & FAQ</Link>
        </div>
        <p>© 2025 VANTAGE</p>
      </footer>
    </div>
  );
};

const App = () => {
  return (
    <ShopProvider>
      <Router>
        <CookieConsent />
        <Storefront />
      </Router>
    </ShopProvider>
  );
};

export default App;